import React from 'react';
import { Box } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import JoinInnerIcon from '@mui/icons-material/JoinInner';
import PersonIcon from '@mui/icons-material/Person';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import NotificationsIcon from '@mui/icons-material/Notifications';
import FolderIcon from '@mui/icons-material/Folder';
import HexagonIcon from '@mui/icons-material/Hexagon';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GppGoodIcon from '@mui/icons-material/GppGood';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import { dsVar } from '../theme/tokens';

// Shared visual vocabulary for React Flow nodes, driven by the `scope` and
// `interaction_mode` attributes now present on each flow-template node.
// Used by both AgentFlowEditor and TaskFlowEditor so the two editors stay in
// sync while keeping their own base node shapes.
//
// Palette is intentionally restrained/business-like: colour is confined to thin
// borders and small chips/badges (no bright fills or coloured glows).

// scope -> corner badge icon + human-readable label. Muted mono accent so the
// badge reads without adding visual noise; the icon carries the meaning.
const SCOPE_BADGE_COLOR = '#94A3B8';
export const scopeConfig = {
  POPULATION: { Icon: PublicIcon, label: 'Population' },
  SEGMENT: { Icon: JoinInnerIcon, label: 'Segment' },
  CUSTOMER: { Icon: PersonIcon, label: 'Customer' },
  ACCOUNT: { Icon: CreditCardIcon, label: 'Account' },
  ALERT: { Icon: NotificationsIcon, label: 'Alert' },
  CASE: { Icon: FolderIcon, label: 'Case' },
  ENTITY: { Icon: HexagonIcon, label: 'Entity' },
  TRANSACTION: { Icon: SwapHorizIcon, label: 'Transaction' },
  DOCUMENT: { Icon: DescriptionIcon, label: 'Document' },
};

// interaction_mode -> shape + colour vocabulary. Reinforcing channels so the
// modes are distinguishable without relying on colour alone (accessibility):
//   shape  - 'standard' | 'attention' | 'gateway' (gateway renders a diamond)
//   icon   - inline icon shown in the mode chip / diamond (shape signal)
//   label  - text shown in the mode chip (the accessible anchor)
//   fill   - the canonical reinforcement colour (secondary-reinforcement table),
//            used for the node's left-edge accent (rail / icon block)
//   stroke - a muted, business-toned accent used for borders/text; chosen to
//            read on the dark canvas while staying desaturated
//   border - 'solid' | 'dashed'
//   weight - border thickness (restrained)
export const interactionModeConfig = {
  AUTONOMOUS: {
    shape: 'standard',
    icon: SmartToyIcon,
    label: 'Autonomous',
    fill: '#4B5563', // slate blue — neutral, no human involvement implied
    stroke: '#7C8B9E', // muted slate
    border: 'solid',
    weight: '1px',
  },
  ASSISTED: {
    shape: 'standard',
    icon: Diversity3Icon,
    label: 'Assisted',
    fill: '#028090', // teal — collaborative / co-pilot
    stroke: '#4F9AA2', // muted teal
    border: 'dashed',
    weight: '1.5px',
  },
  SUPERVISED: {
    shape: 'attention',
    icon: VisibilityIcon,
    label: 'Supervised',
    fill: '#C97B1A', // amber — caution, human review expected
    stroke: '#C0923F', // muted ochre/amber
    border: 'solid',
    weight: '2px',
  },
  APPROVAL_REQUIRED: {
    shape: 'gateway',
    icon: GppGoodIcon,
    label: 'Approval',
    fill: '#C05621', // coral — stop and decide, high stakes
    stroke: '#C07254', // muted terracotta
    border: 'solid',
    weight: '2px',
  },
  HUMAN_ONLY: {
    shape: 'gateway',
    icon: PersonIcon,
    label: 'Human only',
    fill: '#0D1B2A', // deep navy — human-driven
    stroke: '#7C93B5', // muted steel blue
    border: 'solid',
    weight: '2px',
  },
};

// Resolve an interaction_mode to its style tokens, defaulting to AUTONOMOUS for
// missing/unknown values (e.g. older templates).
export const getInteractionMode = (mode) =>
  interactionModeConfig[mode] || interactionModeConfig.AUTONOMOUS;

// Small absolutely-positioned badge showing the node's data scope. Position can
// be overridden (the diamond node anchors it inside the upper-right face).
// Returns null when scope is absent/unknown so older templates render as before.
export const ScopeBadge = ({ scope, top = -9, right = -9, left, bottom }) => {
  const cfg = scopeConfig[scope];
  if (!cfg) return null;
  const { Icon, label } = cfg;
  return (
    <Box
      title={label}
      sx={{
        position: 'absolute',
        top,
        right,
        left,
        bottom,
        width: 22,
        height: 22,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: dsVar('page'),
        border: `1.5px solid ${SCOPE_BADGE_COLOR}`,
        color: SCOPE_BADGE_COLOR,
        boxShadow: '0 2px 6px rgba(0,0,0,0.45)',
        zIndex: 6,
        '& svg': { fontSize: 13 },
      }}
    >
      <Icon fontSize="inherit" />
    </Box>
  );
};

// Icon + text chip that names the interaction_mode. This is the primary,
// colour-independent differentiator (text + icon), overlapping the node's top
// edge. `centered` is used by the diamond node; standard nodes anchor it left.
// Returns null for unknown modes.
export const ModeLabelChip = ({ mode, centered = false }) => {
  const cfg = interactionModeConfig[mode];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <Box
      title={`Interaction mode: ${cfg.label}`}
      sx={{
        position: 'absolute',
        top: -11,
        left: centered ? '50%' : 12,
        transform: centered ? 'translateX(-50%)' : 'none',
        height: 20,
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        px: '8px',
        borderRadius: '10px',
        backgroundColor: dsVar('page'),
        border: `1px solid ${cfg.stroke}`,
        color: cfg.stroke,
        boxShadow: '0 2px 6px rgba(0,0,0,0.5)',
        zIndex: 7,
        whiteSpace: 'nowrap',
        '& svg': { fontSize: 12 },
      }}
    >
      {Icon && <Icon fontSize="inherit" />}
      <Box
        component="span"
        sx={{
          fontSize: '9px',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          fontFamily: "'JetBrains Mono', monospace",
        }}
      >
        {cfg.label}
      </Box>
    </Box>
  );
};

// Icon shown in a node's main icon slot. Gateway modes (APPROVAL_REQUIRED /
// HUMAN_ONLY) surface their gate icon so a decision/approval node is legible at
// a glance while keeping the same card shape as every other node; other modes
// fall back to the editor-supplied default (agent / task icon).
export const getNodeGlyph = (mode, fallbackIcon) => {
  const cfg = interactionModeConfig[mode];
  return cfg && cfg.shape === 'gateway' && cfg.icon ? cfg.icon : fallbackIcon;
};
